import React from 'react'
// libs
import { useSnackbar } from 'notistack'
// components
import { Modal } from 'components/UI'
import Header from 'components/UI/Modal/Header'
import BusinessEntityForm, { BusinessEntityFields } from 'components/FormElements/ReduxForm/Forms/BusinessEntitiesForm'
// constants
import { ENTITIES, Modals } from 'constants/index'
// hooks
import { useModalData } from 'hooks'
// types
import { Place } from 'components/FormElements/LocationPicker'
import { DocumentNode } from 'graphql/language'
// utils
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { camelCase, get, omit, startCase, toLower } from 'lodash'

interface Props {
  query: DocumentNode
  refetch: any
  getOptions: (data: any) => void
  entityType: keyof typeof ENTITIES
  handleSearch: ({ search, refetch }: { search: string; refetch: () => Promise<void> }) => void
  lastPosition: number
  createMutation: any
  updateMutation: any
}

const BusinessEntityModal: React.FC<Props> = ({
  query,
  refetch,
  getOptions,
  entityType,
  handleSearch,
  lastPosition,
  createMutation,
  updateMutation
}) => {
  const {
    modalData: { width, isOpen, parent, item },
    hideModal
  } = useModalData({ modalType: Modals.BUSINESS_ENTITY_MODAL })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const onUpdate = async (data: BusinessEntityFields) => {
    const { active, code, description, parent, address } = data

    try {
      await updateMutation({
        variables: {
          id: item.id,
          [camelCase(entityType)]: {
            code,
            active,
            description,
            parentId: get(parent, 'value', null),
            ...(entityType === ENTITIES.LOCATION && address?.value && { geoLocation: address.value })
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[BusinessEntityModal.handleFormSubmit]: ', parseGraphQLErrorMessages(errors))

      enqueueSnackbar(parseGraphQLErrorMessages(errors), {
        variant: 'warning',
        autoHideDuration: null,
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }
  }

  const onCreate = async (data: BusinessEntityFields) => {
    const { active, code, description, parent, address } = data

    try {
      await createMutation({
        variables: {
          [camelCase(entityType)]: {
            code,
            active,
            description,
            parentId: get(parent, 'value', null),
            position: lastPosition + 1,
            ...(entityType === ENTITIES.LOCATION && address?.value && { geoLocation: address.value })
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[BusinessEntityModal.handleFormSubmit]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const onFormSubmit = async (data: BusinessEntityFields) => {
    if (item) {
      await onUpdate(data)
    } else {
      await onCreate(data)
    }

    hideModal()
  }

  const getInitialValues = () => {
    const initialValues: Partial<BusinessEntityFields> = {}

    if (item) {
      initialValues.code = item.code
      initialValues.active = item.active
      initialValues.description = item.description

      if (item.parent) {
        initialValues.parent = {
          value: item.parent.id,
          label: item.parent.description
        }
      }

      if (entityType === ENTITIES.LOCATION && item.geoLocation) {
        initialValues.lat = item.geoLocation.latitude
        initialValues.long = item.geoLocation.longitude
        initialValues.address = { value: omit(item.geoLocation as Place, '__typename') }
      }
    } else {
      initialValues.active = true

      if (parent) {
        initialValues.parent = {
          value: parent.id,
          label: parent.description
        }
      }
    }
    return initialValues
  }

  return (
    <Modal hideModal={hideModal} isOpen={isOpen} width={width}>
      <Header handleClearClick={hideModal}>{`${item ? 'Update' : 'Create'} ${startCase(toLower(entityType))}`}</Header>
      <BusinessEntityForm
        query={query}
        entityId={item?.id}
        hideModal={hideModal}
        onSubmit={onFormSubmit}
        entityType={entityType}
        getOptions={getOptions}
        handleSearch={handleSearch}
        initialValues={getInitialValues()}
      />
    </Modal>
  )
}

export default BusinessEntityModal
